import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from './AuthContext';
import { api } from '../services/api';

interface NotificationContextType {
  unreadCount: number;
  showNotificationPopup: boolean;
  newMatchStudent: { prenom: string; nom: string } | null;
  markAsRead: () => void;
  refreshNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotificationPopup, setShowNotificationPopup] = useState(false);
  const [newMatchStudent, setNewMatchStudent] = useState<{ prenom: string; nom: string } | null>(null);

  // Charger le nombre de demandes non lues
  const loadUnreadCount = React.useCallback(async () => {
    if (!user) return;

    try {
      // Récupérer le profil tuteur de l'utilisateur
      const { data: profile, error: profileError } = await api.profiles.getByUserId(user.id);
      
      if (profileError || !profile) return;

      // Compter les matchs en attente (like_etudiant = true, like_tuteur = false, ET non refusés)
      const { count, error } = await supabase
        .from('matchs')
        .select('*', { count: 'exact', head: true })
        .eq('repetiteur_id', profile.id)
        .eq('like_etudiant', true)
        .eq('like_tuteur', false)
        .neq('liked', false);  // Exclure les demandes refusées

      if (!error && count !== null) {
        setUnreadCount(count);
      }
    } catch (err) {
    }
  }, [user]);

  // Écouter les nouveaux matchs en temps réel
  useEffect(() => {
    if (!user) return;

    let subscription: any;

    const setupRealtimeSubscription = async () => {
      try {
        // Récupérer le profil tuteur
        const { data: profile, error: profileError } = await api.profiles.getByUserId(user.id);
        
        if (profileError || !profile) return;

        // S'abonner aux changements dans la table matchs pour ce tuteur
        subscription = supabase
          .channel('match-notifications')
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'matchs',
              filter: `repetiteur_id=eq.${profile.id}`
            },
            async (payload) => {
              // Récupérer les infos de l'étudiant qui a liké
              const { data: studentProfile, error: studentError } = await supabase
                .from('profils')
                .select('prenom, nom')
                .eq('user_id', payload.new.etudiant_id)
                .single();

              if (!studentError && studentProfile) {
                setNewMatchStudent({
                  prenom: studentProfile.prenom,
                  nom: studentProfile.nom
                });
                setShowNotificationPopup(true);
                setUnreadCount(prev => prev + 1);

                // Masquer la popup après 5 secondes
                setTimeout(() => {
                  setShowNotificationPopup(false);
                }, 5000);
              }
            }
          )
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'matchs',
              filter: `repetiteur_id=eq.${profile.id}`
            },
            (payload) => {
              // Recharger le compteur quand un match est accepté/refusé
              loadUnreadCount();
            }
          )
          .subscribe();

      } catch (err) {
      }
    };

    setupRealtimeSubscription();
    loadUnreadCount();

    return () => {
      if (subscription) {
        supabase.removeChannel(subscription);
      }
    };
  }, [user, loadUnreadCount]);

  const markAsRead = () => {
    setShowNotificationPopup(false);
    setNewMatchStudent(null);
  };

  const refreshNotifications = () => {
    loadUnreadCount();
  };

  const value: NotificationContextType = {
    unreadCount,
    showNotificationPopup,
    newMatchStudent,
    markAsRead,
    refreshNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};